import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { CreditLimitStateType } from 'app/routes/credit-limit/credit-limit.model';

export type TextClassRule = {
  className: string;
  includes: string[];
};

export const STATUS_TEXT_CLASS_RULES: TextClassRule[] = [
  { className: 'status-tag-red', includes: ['rejected', 'inactive', 'expired', 'high'] },
  { className: 'status-tag-green', includes: ['approved', 'completed', 'accepted', 'signed', 'low', 'active'] },
  { className: 'status-tag-grey', includes: ['draft', 'cancelled', 'pending_hoc', 'pending hoc'] },
  { className: 'status-tag-disable', includes: ['not updated'] }
];

export function getTextClassByIncludes(
  value: string,
  classRules: TextClassRule[],
  defaultClass = ''
): string {
  const normalizedValue = (value || '').toLowerCase();
  const matchedRule = classRules.find((rule) => rule.includes.some((keyword) => normalizedValue.includes(keyword)));
  return matchedRule?.className || defaultClass;
}

export function getStatusTagTextClass(status: string): string {
  return getTextClassByIncludes(status, STATUS_TEXT_CLASS_RULES, 'status-tag-yellow');
}

@Injectable({
  providedIn: 'root'
})

/**
 * Constants for the project. Stored in service so can be easily replaced.
 */
export class ConstantsService {

  // this is the mock backend they setup
  public baseURL = 'http://18.191.0.111:3007';
  public inputPatternOnlyNumbers = new RegExp(/^[0-9,]*$/);
  public inputPattern = new RegExp(`^(?!0+$)\\d+(\\.{1}\\d{0,2}){0,1}$`);
  public inputPatternWithTwoDecimal = new RegExp(`^(?!$)\\d{0,15}(?:\\.\\d{0,2})?$`);
  public inputPatternWithNegativeAndTwoDecimal = new RegExp(/^[-]?\d{0,15}(\.\d{0,2})?$/);
  public percentagePattern = new RegExp(`^(?!$)\\d{0,3}(?:\\.\\d{0,2})?$`);
  public percentagePatternUpto4Decimal = new RegExp(`^(?!$)\\d{0,3}(?:\\.\\d{0,4})?$`);
  public inputPatternWithFiveDecimal = new RegExp(`^(?!$)\\d{0,15}(?:\\.\\d{0,5})?$`);
  public inputPatternWithSingleZero = new RegExp(`^([1-9][\d]*|0)+(\\.{1}\\d{0,2}){0,1}$`);
  public inputPatternWithThreeDigits = new RegExp(/^[0-9]{0,3}$/);
  public inputPatternWithSingleAlphabet = new RegExp(/^[A-Z]{1}$/);
  public inputPatternWithAlphaNumericAndSpace = new RegExp(/^[a-zA-Z0-9 ]*$/);
  public inputPatternWithAlphaNumeric = new RegExp(/^[a-zA-Z0-9]*$/);
  public peppolIdPattern = new RegExp(/^\d{4}:[a-zA-Z0-9\-.]{1,50}$/);
  public peppolIdPatternErrorMessage = 'Invalid Peppol ID. Expected format: 4-digit scheme code, colon, then up to 50 alphanumeric characters. Example: 0208:1018265814';
  public peppolIdHintPatternMessage = 'Expected format: 4-digit scheme code, colon, then up to 50 alphanumeric characters. Example: 0208:1018265814';
  public peppolIdTooltip = 'Enter your Peppol Participant ID in the format NNNN:identifier (scheme code colon identifier).';
  public emailReg = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  public phoneReg = new RegExp(/^[0-9]*$/);
  public fileNameReg = new RegExp(/[!@#\$\^\&\%\)\(\\\-`+,.\/"]/);
  public kycRemarksRegex = new RegExp('^[\\w\\.\\,\\-\\s]+$');
  public inputPatternWithSixDecimal = new RegExp(`^(?!$)\\d{0,15}(?:\\.\\d{0,6})?$`);
  public inputPatternWithNoSpecialChar = new RegExp(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/);
  public widgetId = environment.production ? '1000000' : '1000005';

  public directories = {
    // dashboard stuff
    transactions: '/transactions',       // GET
    finances: '/finances',               // GET
    creditLimit: '/creditLimit',         // GET
    notifications: '/:user/notifications', // GET
    updateNotifications: '/update/readstatus'  // POST
  };

  public terminals = [
    'Seattle',
    'Dubai',
    'Shanghi',
  ];

  public proofTypes = [
    'Type1',
    'Type2',
    'Type3',
  ];

  public banks = [
    'Bank1',
    'Bank2'
  ];

  public currencys = [
    'USD',
    'AED',
  ];

  public transactionTypes = [
    'Account Settled',
  ];

  public statuses = [
    'Cargo inspected positive',
    'Bill of Lading submitted',
    'Vessel departed'
  ];


  public stateToIndex = {
    BCO_FINANCE_SAVED: 0,
    DTF_FINANCE_ACCEPTED: 3,      // Bank Assessment (Banker review)
    BANKS_FINANCE_ASSESSED: 4,    // Accept Terms (BCO's review terms)
    BCO_TERMS_AGREED: 5,          // Trade Finance Approved
    APPROVED_BY_MAKER: 3,
    REWORK_PENDING_BY_MAKER: 3,
    FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER: 3,
    BANK_FUNDS_TRANSFERRED: 5,
    FUNDS_RETURNED_UPDATED_BY_MAKER: 5,
    FUNDS_RETURNED_UPDATED_BY_CHECKER: 5,
    FUNDS_RETURNED: 6,
    ACCOUNT_SETTLED: 6,           // Account Settled
    DTF_FINANCE_CLOSED: 6,        // Account Settled
    FINANCE_CANCELLED: 7,          // Account Settled
    REJECTED_BY_FI: 5,
    DETAILS_REQUIRED: 0,
    DUE_DATE_UPDATE_REQUESTED: 5,
    CONVERSION_REQUEST_PENDING_CLIENT_RESUBMISSION: 0,
    CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION: 3,
    CONVERSION_REQUEST_PENDING_MAKER_REVIEW: 3,
    CONVERSION_REQUEST_PENDING_CHECKER_REVIEW: 3,
    CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION: 0
  };

  public stateToIndexLogistics = {
    BCO_FINANCE_SAVED: 0,
    DTF_FINANCE_ACCEPTED: 1,      // Bank Assessment (Banker review)
    APPROVED_BY_MAKER: 1,
    REWORK_PENDING_BY_MAKER: 1,
    FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER: 1,
    BANKS_FINANCE_ASSESSED: 2,    // Accept Terms (BCO's review terms)
    BCO_TERMS_AGREED: 3,          // Logistics Finance Approved
    BANK_FUNDS_TRANSFERRED: 4,    //
    FUNDS_RETURNED_UPDATED_BY_MAKER: 4,
    FUNDS_RETURNED_UPDATED_BY_CHECKER: 4,
    FUNDS_RETURNED: 5,
    ACCOUNT_SETTLED: 5,           // Account Settled
    DTF_FINANCE_CLOSED: 5,        // Account Settled
    FINANCE_CANCELLED: 6,        // Account Settled
    IMPORTER_APPROVAL_PENDING: 2,
    DETAILS_REQUIRED: 0,
    PARTIALLY_SETTLED: 7,
    WAIVER_REQUEST: 7,
    WAIVER_REQUEST_REJECTED: 7,
    REJECTED_BY_FI: 3,
    DUE_DATE_UPDATE_REQUESTED: 5,
    FUNDS_TRANSFER_UPDATED_BY_MAKER: 4,
    FUNDS_TRANSFER_PENDING_REWORK: 4,
    SETTLEMENT_UPDATED_BY_MAKER: 5,
    PARTIAL_SETTLEMENT_UPDATED_BY_MAKER: 7,
    PARTIAL_SETTLEMENT_PENDING_REWORK: 7,
    SETTLEMENT_PENDING_REWORK: 7,
    BALANCE_SETTLEMENT_UPDATED_BY_MAKER: 7,
    BALANCE_SETTLEMENT_PENDING_REWORK: 7,
    OVERDUE_WAIVE_OFF_REQUESTED: 7,
    REJECT_IN_PROGRESS: 1,
    INTEREST_SCHEDULE_UPDATE_REQUESTED: 1,
    INTEREST_SCHEDULE_REWORK_PENDING: 1,
    CM_REVIEW_PENDING_OM_CHECKER: 1,
    CM_REWORK_PENDING_OM_MAKER: 1,
    CM_REVIEW_PENDING_FM: 1,
    CM_RESUBMISSION_PENDING_FM_CHECKER: 1,
    CM_REWORK_PENDING_OM_CHECKER: 1,
    CONVERSION_REQUEST_PENDING_CLIENT_RESUBMISSION: 0,
    CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION: 1,
    CONVERSION_REQUEST_PENDING_MAKER_REVIEW: 1,
    CONVERSION_REQUEST_PENDING_CHECKER_REVIEW: 1,
    CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION: 0,
    POST_SHIPMENT_CONVERSION_IN_PROCESS: 4,
  };

  public shownAcceptedTerms = ['DETAILS_REQUIRED', 'CM_RESUBMISSION_PENDING_FM_CHECKER', 'CM_REWORK_PENDING_OM_CHECKER', 'CM_REVIEW_PENDING_FM', 'CM_REVIEW_PENDING_OM_CHECKER', 'CM_REWORK_PENDING_OM_MAKER', 'INTEREST_SCHEDULE_UPDATE_REQUESTED', 'INTEREST_SCHEDULE_REWORK_PENDING', 'POST_SHIPMENT_CONVERSION_IN_PROCESS']
  applicantTypes = [
    'importer',
    'exporter',
  ];

  importerDealingCommodity = [
    '<1 year',
    '1 year',
    '2 years',
    '3 years',
    '4 years',
    '5 years',
    '6 years',
    '>6 years'
  ];
  delayOnPastPayments = [
    { value: 'Perfect Account Conduct', viewValue: 'Perfect Account Conduct' },
    { value: 'Satifactory Account Conduct with minor delays', viewValue: 'Satifactory Account Conduct with minor delays' },
    { value: 'Often major delays in payments', viewValue: 'Often major delays in payments' },
    { value: 'Defaulted in the past', viewValue: 'Defaulted in the past' },
  ];
  turnOver = [
    'Up to $10 million',
    '$10-40 million',
    'Greater than  $40 million'
  ]

  public publicApiListForExtBCOTermsAcceptance = [
    'get-credit-limit-data-public',
    'docusign-link-public',
    'credit-limit-terms-acceptance-page',
    'credit-limit/document-public',
    'docusign-recipient-details-public',
    'docusign-documents-public',
    'accept-credit-limit-terms-public',
    'logistics-finance/finance-terms-public',
    'logistics-finance/document-public',
    'logistics-finance/accept-terms-public',
    'accept-credit-facility-terms-public',
    'documents-public'
  ];

  public carrierNamesList: string[] = [
    "Valid ocean lines",
    "ACL",
    "ANL",
    "APL",
    "Alianca",
    "Arkas",
    "Avana",
    "CMA-CGM",
    "CNC",
    "COSCO",
    "CUL",
    "Delex",
    "ESL",
    "Evergreen",
    "HMM",
    "Hamburg Sud",
    "Hapag Lloyd",
    "Heung-A",
    "KMTC",
    "MEL",
    "MSC",
    "Maersk",
    "Matson",
    "NYK",
    "Namsung",
    "ONE",
    "OOCL",
    "PIL",
    "Perma",
    "RCL",
    "SITC",
    "SML",
    "SPIL",
    "Safmarine",
    "Samudera",
    "Sealand",
    "Seatrade",
    "Sinokor",
    "Sinotrans",
    "Turkon",
    "Unifeeder",
    "WEC",
    "Wan Hai",
    "Westwood",
    "Yang Ming",
    "ZIM"
  ];

  public applyCreditLimitTooltips = {
    currency: 'The currency of invoice and the currency of disbursement.',
    expectedCreditLimit: 'The credit limit expected from the Financial Institution.',
    minCreditLimit: `The minimum credit limit required without which the applicant won't accept the credit facility terms.`,
    minCreditPeriodExpected: 'The minimum no. of days you would require the credit amount for.'
  };

  public creditLimitDocTypes: string[] = [
    'docusign_credit_limit_accept_terms_data_buyer', 'docusign_credit_limit_assessment_data_buyer',
    'docusign_credit_limit_accept_terms_data_seller', 'docusign_credit_limit_assessment_data_seller',
    'credit_limit_assessment_data_seller', 'credit_limit_accept_terms_data_seller',
    'credit_limit_assessment_data_buyer', 'credit_limit_accept_terms_data_buyer', 'counter_party_document',
    'docusign_accept_term_sheet', 'accept_term_sheet', 'termSheetTermsDocument', 'docusign_termSheetTermsDocument'
  ];

  public stateToName = {
    BCO_FINANCE_SAVED: 'Draft',
    DTF_FINANCE_ACCEPTED: 'Under Assessment',      // Financial Institution Assessment (Banker review)
    BANKS_FINANCE_ASSESSED: 'Financial Institution Assessed',    // Accept Terms (BCO's review terms)
    BCO_TERMS_AGREED: 'Terms Accepted',          // Trade Finance Approved
    BANK_FUNDS_TRANSFERRED: 'Funds Transferred',
    FUNDS_RETURNED_UPDATED_BY_MAKER: 'Funds Returned Updated By Maker',
    FUNDS_RETURNED_UPDATED_BY_CHECKER: 'Funds Returned Updated By Checker',
    FUNDS_RETURNED: 'Funds Returned',
    ACCOUNT_SETTLED: 'Account Settled',
    DTF_FINANCE_CLOSED: 'Application Closed',        // Account Settled
    FINANCE_CANCELLED: 'Cancelled',          // Account Settled,
    DETAILS_REQUIRED: 'Details Required',
    IMPORTER_APPROVAL_PENDING: 'Importer Approval Pending',
    APPROVED_BY_MAKER: 'Approved By Maker',
    REJECT_IN_PROGRESS: 'Reject In Progress',
    REWORK_PENDING_BY_MAKER: 'Rework Pending By Maker',
    FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER: 'Finance Assessment requested for defaulter',
    REJECTED_BY_APPLICANT: 'Rejected By Applicant',
    REJECTED: 'Rejected',
    OVERDUE_WAIVE_OFF_REQUESTED: 'Overdue Waive Off Requested',
    ACCOUNT_SETTLED_BY_FI: 'Account Settled By FI',
    PARTIALLY_SETTLED: 'Partially Settled',
    WAIVER_REQUEST: 'Waiver Request',
    WAIVER_REQUEST_REJECTED: 'Waiver Request Rejected',
    REJECTED_BY_FI: 'Rejected By Financial Institution',
    DUE_DATE_UPDATE_REQUESTED: 'Due Date Update Requested',
    APPROVED_BY_CREDIT_MAKER: 'Approved By Credit Maker',
    REWORK_PENDING_BY_CREDIT_MAKER: 'Rework Pending By Credit Maker',
    FUNDS_TRANSFER_UPDATED_BY_MAKER: 'Funds Transfer Updated By Maker',
    FUNDS_TRANSFER_PENDING_REWORK: 'Funds Transfer Pending Rework',
    PARTIAL_SETTLEMENT_PENDING_REWORK: 'Partial Settlement Pending Rework',
    PARTIAL_SETTLEMENT_UPDATED_BY_MAKER: 'Partial Settlement Updated By Maker',
    SETTLEMENT_UPDATED_BY_MAKER: 'Settlement Updated by Maker',
    SETTLEMENT_PENDING_REWORK: 'Settlement Pending Rework',
    BALANCE_SETTLEMENT_UPDATED_BY_MAKER: 'Balance Settlement Updated by Maker',
    BALANCE_SETTLEMENT_PENDING_REWORK: 'Balance Settlement Pending Rework',
    DTF_FINANCE_SETTLED: 'DTF Finance Settled',
    SETTLED_WITH_OVERDUE: 'Settled With Overdue',
    INTEREST_SCHEDULE_UPDATE_REQUESTED: 'Interest Schedule Updated by Maker',
    INTEREST_SCHEDULE_REWORK_PENDING: 'Interest Schedule Rework pending with Maker',
    CM_REVIEW_PENDING_OM_CHECKER: 'Credit Note Review Pending with Operations Checker',
    CM_REWORK_PENDING_OM_MAKER: 'Credit Note Rework Pending with Operations Maker',
    CM_REVIEW_PENDING_FM: 'Credit Note Review Pending with Finance Manager',
    CM_RESUBMISSION_PENDING_FM_CHECKER: 'Credit Note Accounting Resubmission Pending with Finance Checker',
    CM_REWORK_PENDING_OM_CHECKER: 'Credit Note Rework Pending with Operations Checker',
    CM_AWAIT_ACCOUNTING_APPROVE: 'Credit Note Awaiting Accounting Approval',
    CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION: 'Conversion Request Pending Client Submission',
    CONVERSION_REQUEST_PENDING_MAKER_REVIEW: 'Conversion Request Pending Maker Review',
    CONVERSION_REQUEST_PENDING_CHECKER_REVIEW: 'Conversion Request Pending Checker Review',
    CONVERSION_REQUEST_PENDING_CLIENT_RESUBMISSION: 'Conversion Request Pending Client Resubmission',
    CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION: 'Conversion Request Pending Maker Resubmission',
    CONVERSION_REQUEST_REJECTED_BY_MAKER: 'Conversion Request Rejected by Maker',
    CONVERSION_REQUEST_REJECTED_BY_CHECKER: 'Conversion Request Rejected by Checker',
    CONVERTED_TO_POST_SHIPMENT: 'Converted to Post Shipment',
    POST_SHIPMENT_CONVERSION_IN_PROCESS: 'Post Shipment Conversion in Process'
  };

  public transactionsBulkUploadHeadersFormat = [
    "Application Number",
    "Transaction Event Type (Disbursal, Settlement, Settlement_by_fi)",
    "Transaction Event Date (MM-DD-YYYY)",
    "Debit from A/C no",
    "Credit to A/C No",
    "Payment Mode",
    "Other Charges Type",
    "Other Charges Amount",
    "Remarks"
  ];

  public applicationsBulkUploadHeadersFormat = [
    "Application Number",
    "Status (Approve, Reject, Request Information)",
    "Approved Receivable Amount",
    "Actual Disbursal Date (MM-DD-YYYY)",
    "Financial Institution Bank Account Number",
    "Financial Institution Bank Name",
    "Financial Institution Bank Branch Address",
    "Financial Institution Swift Code",
    "Financial Institution Country",
    "Financial Institution State",
    "Financial Institution City",
    "Financial Institution Address",
    "Financial Institution Pincode",
    "Financial Institution IBAN/IFSC Code/Branch Code/Routing Number/Routing Transit Number",
    "Remarks"
  ];

  public fiDataBulkUploadErrorHandler = {
    INTERNAL_SERVER_ERROR: 'Error occured while bulk updating FI data',
    PARTIAL_DATA_UPLOADED: 'Error in updating few records. Please check error files for details and upload those records again post correction.',
    SUCCESSFUL_DATA_UPLOAD: 'All records are updated',
    EMPTY_FILE_UPLOADED: 'Empty file uploaded, Please check "File Format".',
    WRONG_FORMAT_FILE_UPLOADED: 'File with wrong format uploaded, Please check "File Format".',
    MULTIPLE_FILE_UPLOAD: 'Cannot upload multiple files',
    STATUS_OR_EVENT_TYPE_MISSING: 'Status/Transaction Event Type Missing.',
    APPLICATION_NUMBER_MISSING: 'Application Number Missing.',
    REMARKS_MISSING: 'Remarks required for Status justification.',
    ALL_EXCEPT_REMARKS_REQUIRED: 'All information except Remarks is mandatory.',
    OTHER_CHARGES_ERROR: 'Other Charges Type and Other Charges Amount should both be either empty or non-empty.',
    ALL_EXCEPT_OTHER_CHARGES_REQUIRED: 'All fields except Other Charges Type and Amount are mandatory.',
    WRONG_APPLICATION_STATUS: 'Application status can only be one of [Approve, Reject, Request Information]',
    WRONG_TRANSACTION_TYPE: 'Transaction Event Type can only be one of [Disbursal, Settlement, Settlement_by_fi]',
  }

  public companyOnboardingKycQuestions = {
    EXISTING_DPWORLD_CUSTOMER: 'Are you an existing customer of the DP World Group?',
    COMMERTIAL_TRADE_BUSSENESS: 'Do you have trade with any government or state-owned entities?',
    COUNTRY_REFERENCE: 'Please select reference countries, if you have trade with any government or state-owned entities:',
    WEALTH_SOURCE: 'Source of wealth:',
    LICENCED_CREDIT_PROVIDER: 'Are you licenced and regulated credit provider?',
    FINANCING_PROCUDURES_CHECK: 'Do you have procedures in place to detect and prevent money laundering and terrorist financing?',
    ASSOCIATED_COUNTRIES_FOR_BORROWERS: 'List countries of incorporation of your top 5 borrowers in order of loan value:',
    COUNTRIES_FOR_BORROWERS_COUNTERPARTIES: 'List top 5 countries of incorporation of your borrower’s counterparties in order of loan value:',
    FINANCE_COMMODITY_TYPES: 'List top 3 products, goods or commodities you finance:',
    FINANCE_TYPE_OPTION: 'Select the type of financing you provide:',
    COUNTRY_OF_ORIGIN_SELLER: 'Select countries of origin, i.e., seller countries you want to provide finance on DP World Trade Finance:',
    COUNTRY_OF_DESTINATION_BUYER: 'Select countries of destination, i.e., buyer countries you want to provide finance on DP World Trade Finance:',
    BORROWER_PROCESSING_CHARGE: 'Enter transaction fee range you charge to your borrowers on each invoice for financing:',
    BORROWER_DISCOUNT_RATE: 'Enter interest rate range you charge to your borrowers on each invoice for financing:',
    BORROWER_CREDIT_FACILITY: 'Select documents needed by you from borrowers to onboard and extend credit facility:',
    FINANCE_TYPE: 'Select the type of financing you are interested in?',
    BUSINESS_SECTOR: 'Select sectors of your business',
    DP_WORLD_COMPANY: 'Select DP world company you are existing customer with:',
    ORG_REG_UNDER: 'Is organization registered under DIFC?',
    ASSOCIATED_YEARS: 'From how many years you are associated with DP world group?',
    ANNUAL_TRANSACTIONS: 'Expected annual transaction volumes/annual turnover',
    TRANSACTIONS_COUNT: 'Expected number of transactions per month on DP World Trade Finance',
    MONTHLY_TRANSACTION: 'Expected monthly transaction value on DP World Trade Finance',
    SERVICE_SECTORS: 'List top 3 sectors you service',
    COMMODITY_TYPES: 'What goods or commodities do you trade in',
    CONSOLIDATION_WITH_CARGOES: 'Do you consolidate cargoes with other companies?',
    COUNTRIES_OF_ORIGIN: 'List countries of origin of major goods or commodities you trade in',
    COUNTRIES_OF_DESTINATION: 'List countries of destination of major goods or commodities you trade in',
    COMPANY_LISTED_EXCHANGE: 'Is your company listed on an exchange?',
    EXCHANGE_ISIN: 'Provide name of exchange and ISIN code, if listed',
    TRADING_NAME: 'Trading Name (if different from Full Legal Name)',
    BUSINESS_ADDRESS: 'Business Address if different from Registered Address',
    EXTERNAL_AUDITOR_NAME: 'Name and address of External Auditor',
    POLITICALLY_EXPOSED_PERSON: 'Is the company exposed to a politically exposed person?',
    COMPANY_WEBSITE: 'Website details of company',
    COMPANY_REGISTRATION: 'Date of incorporation or registration of Company',
    COMPANY_FINANCE_TYPE: 'Are you NBFC or Bank?',
    FATF_MEMBER: 'Are you incorporated in a FATF member country?',
    PERMISSIONS_CHECK_FOR_SERVICES_OFFERED: 'Do you require, and do you have all commercial, operating or regulatory licenses, authorisations, consents, approvals and permits required under the laws applicable to you or the products/ services you offer?',
    FINANCE_SECTORS: 'List top 3 sectors you finance',
    ECONOMIC_SECTOR: 'Which economic sector does the company primarily deal in?',
    BUSINESS_TENURE: 'Tenure of the business?',
    JURISDICTION_OF_OPERATION: 'Jurisdiction of operation?',
    CLIENT_RELATIONSHIP: 'Is the client known to the Firm / RM and/or the Group ?',
    EXPERIENCE_IN_TRADE_FINANCE: 'Length of dealing in Trade Finance?',
    REGIONS_LIST: 'Select region/s where your borrowers are located:',
    BUSINESS_SECTOR_OTH: 'Other business sectors',
    DP_WORLD_COMPANY_OTH: 'Other company',
    SERVICE_SECTORS_OTH: 'Other service sectors',
    FINANCE_SECTORS_OTH: 'Other finance sector',
    COMMODITY_TYPES_OTH: 'Other commodity',
    FINANCE_COMMODITY_TYPES_OTH: 'Other commodity',
    REGIONS_LIST_OTH: 'Other region',
    ANNUAL_TURNOVER: 'Annual turnover of the company in USD as per latest audited financials?',
  };

  public clientKycQuestions = [
    'EXISTING_DPWORLD_CUSTOMER',
    'COMMERTIAL_TRADE_BUSSENESS',
    'COUNTRY_REFERENCE',
    'WEALTH_SOURCE',
    'ECONOMIC_SECTOR',
    'BUSINESS_TENURE',
    'JURISDICTION_OF_OPERATION',
    'CLIENT_RELATIONSHIP',
    'EXPERIENCE_IN_TRADE_FINANCE',
    'CLIENT_RELATIONSHIP_DURATION',
    'FINANCE_TYPE',
    'BUSINESS_SECTOR',
    'DP_WORLD_COMPANY',
    'ASSOCIATED_YEARS',
    'ANNUAL_TRANSACTIONS',
    'TRANSACTIONS_COUNT',
    'MONTHLY_TRANSACTION',
    'SERVICE_SECTORS',
    'COMMODITY_TYPES',
    'CONSOLIDATION_WITH_CARGOES',
    'COUNTRIES_OF_ORIGIN',
    'COUNTRIES_OF_DESTINATION',
    'COMPANY_LISTED_EXCHANGE',
    'EXCHANGE_ISIN',
    'TRADING_NAME',
    'BUSINESS_ADDRESS',
    'EXTERNAL_AUDITOR_NAME',
    'POLITICALLY_EXPOSED_PERSON',
    'ORG_REG_UNDER',
    'COMPANY_WEBSITE',
    'COMPANY_REGISTRATION',
    'OTHER_BUSINESS_SECTOR',
    'OTHER_DP_WORLD_COMPANY',
    'OTHER_SERVICE_SECTOR',
    'OTHER_FINANCE_SECTOR',
    'otherCommodities',
    'ANNUAL_TURNOVER'
  ]

  public fiKycQuestions = [
    'LICENCED_CREDIT_PROVIDER',
    'FINANCING_PROCUDURES_CHECK',
    'COMPANY_FINANCE_TYPE',
    'COMMERTIAL_TRADE_BUSSENESS',
    'COUNTRY_REFERENCE',
    'FATF_MEMBER',
    'PERMISSIONS_CHECK_FOR_SERVICES_OFFERED',
    'FINANCE_SECTORS',
    'ASSOCIATED_COUNTRIES_FOR_BORROWERS',
    'COUNTRIES_FOR_BORROWERS_COUNTERPARTIES',
    'FINANCE_COMMODITY_TYPES',
    'FINANCE_TYPE_OPTION',
    'REGIONS_LIST',
    'COUNTRY_OF_ORIGIN_SELLER',
    'COUNTRY_OF_DESTINATION_BUYER',
    'BORROWER_PROCESSING_CHARGE',
    'BORROWER_DISCOUNT_RATE',
    'BORROWER_CREDIT_FACILITY',
    'TRADING_NAME',
    'BUSINESS_ADDRESS',
    'EXTERNAL_AUDITOR_NAME',
    'POLITICALLY_EXPOSED_PERSON',
    'COMPANY_WEBSITE',
    'COMPANY_REGISTRATION',
    'OTHER_FINANCE_SECTOR',
    'otherCommodities',
    'OTHER_REGION'
  ]

  public isLicensedAndRegulatedOptions = [
    'LICENCED_AND_REGULATED',
    'LICENCED_AND_BUT_NOT_REGULATED',
    'NOT_LICENCED'
  ];

  public isLicensedAndRegulatedMap = {
    LICENCED_AND_REGULATED: 'Licensed and Regulated',
    LICENCED_AND_BUT_NOT_REGULATED: 'Licensed but not Regulated',
    NOT_LICENCED: 'Not licensed'
  }

  public serviceProvidersList = [
    'DP World Trade Finance',
    'DP World Trade Finance Cayman'
  ]

  public caymanKycDocumentCodeMapping = {
    VTLC: "Trade License or Incorporation Certificate",
    ROS: "Register of Shareholders - Name, Address, Corporate Information, Layer's details",
    POA: "Proof of Authority (POA or Board Resolution)",
    VAT: "VAT",
    AP: "Address Proof",
    CI: "Certificate of Incorporation",
    VTL: "Valid Trade License or Certificate of Incumbency",
    GOC: "Group Organization Chart",
    MAA: "Memorandum & Articles of Association",
    CSC: "Ownership Structure Chart (all Shareholders and UBOs owning or controlling 25% or more)",
    ROD: "Register of Directors",
    DOS: "Details of Shareholders and UBOs owning or controlling 10% or more (Passport copy, Address Proof)",
    IAS: "Identity of Authorised Signatory (Passport copy)",
    PRD: "Passports of Registered Directors (board members)",
    AFAR: "Audited Financials + Annual Report (past 2 years)",
    BAP: "Address Proof",
    BAFAR: "Audited Financials + Annual Report (past 2 years)",
    COI: "Certificate of Incorporation",
    CAF: "Company AML framework",
    BCWFC: "Completed Wolfsberg FCCQ",
    BDOS: "Details of Shareholders and UBOs owning or controlling 25% or more (Passport copy, Address Proof)",
    BGOC: "Group Organization Chart",
    BIAS: "Identity of Authorised Signatory (Passport copy)",
    BIAML: "Information on AML Risk Profiling methodology",
    BMAA: "Memorandum & Articles of Association",
    BCSC: "Ownership Structure Chart (all Shareholders and UBOs owning or controlling 25% or more)",
    BPRD: "Passports of Registered Directors (board members)",
    BPGB: "Proof that entity is Government Body or Non-Commercial Government entity (if relevant)",
    BPOA: "Proof of Authority (POA or Board Resolution)",
    BPOL: "Proof of Listing (if listed)",
    POR: "Proof of Regulation, if regulated",
    BROD: "Register of Directors",
    BROS: "Register of Shareholders (Name, Address, Corporate Information, how many layers)",
    BVTL: "Valid Trade License or Certificate of Incumbency",
  }

  public bankDetailsParamMapping = {
    account_number: 'Account Number',
    bank_name: 'Bank Name',
    bank_branch_code: 'Bank Branch Code',
    bank_branch_address: 'Bank Branch Address',
    swift_code: 'Swift Code',
    country: 'Country',
    province: 'Province',
    city: 'City',
    address: 'Address',
    pin_code: 'Pincode',
  }

  public applyFinanceSupportingDocuments = ['purchase_order', 'proforma_invoice', 'sales_contract', 'master_bill_of_lading', 'seaway_bill',
    'airway_bill', 'truck_consignment_note', 'delivery_note', 'transport_document', 'certificate_of_origin', 'packing_list', 'other_document'
  ]

  public applyFinanceSupportingDocumentsMap = {
    purchase_order: `Purchase Order`,
    proforma_invoice: `Proforma Invoice`,
    sales_contract: `Sales Contract`,
    master_bill_of_lading: 'Master Bill Of Lading',
    seaway_bill: 'Seaway Bill',
    airway_bill: 'Airway Bill',
    truck_consignment_note: 'Truck Consignment Note',
    delivery_note: 'Delivery Note',
    transport_document: 'Transport Document',
    certificate_of_origin: 'Certificate Of Origin',
    packing_list: 'Packing List',
    other_document: 'Other Document'
  }

  public creditLimitRejectStatusTypes = [
    'Rejected By Financial Institution',
    'Rejected By Applicant',
    'Rejected By Counter Party'
  ];

  public creditStatusForDiffRoles = [
    'Facility Documents Reviewed By Operations',
    'Facility Documents Reviewed by Operations & Credit',
    'Approved By Credit Analyst',
    'Approved By Credit Manager',
    'Approved By Head Of Credit',
    'Approved By Credit Team',
    'Rework Pending by Credit Analyst',
    'Under Assessment',
    'Save As Draft',
    'Document Rework Pending',
    'Terms Accepted',
    'Signature Pending',
    'Term Sheet Approved',
    'Term Sheet Approved, KYC Pending',
    'Term Sheet Accepted',
    'Facility Requested',
    'Term Sheet Published'
  ];

  public termSheetStatuses = [
    'Under Assessment',
    'Term Sheet Approved',
    'Term Sheet Approved, KYC Pending',
    'Term Sheet Accepted',
    'Term Sheet Published'
  ];

  public diffRoleReworkPendingStatus = [
    'Rework Pending by Credit Analyst',
    'Document Rework Pending',
    'Signature Pending'
  ];

  public updateFlowDiffRoleReworkPendingStatus = [
    'Limit Update Rework Pending by Credit Analyst',
    'Product Update Rework Pending by Credit Analyst',
    'Limit Update Document Rework Pending',
    'Product Update Document Rework Pending',
    'Agreement Change Document Rework Pending',
    'Limit Update Rework Pending by Operations',
    'Product Update Rework Pending by Operations',
    'Agreement Change Rework Pending by Operations',
    'Renew Rework Pending by Operations',
    'CounterParty Edit Rework Pending by Credit Analyst',
    'Limit Update Signature Pending',
    'Product Update Signature Pending',
    'Agreement Change Signature Pending',
    'Renew Signature Pending',
    'CounterParty Edit Signature Pending',
    'Signature Pending',
    'Facility Update Signature Pending',
  ]

  public mawaniResponseFieldNames = {
    GST_DETAILS: 'gst_details',
    DIRECT_SHAREHOLDERS: 'shareholder_details',
    DIRECTOR_DETAILS: 'director_details',
    BENEFICIAL_OWNERS: 'beneficial_owners',
    SENIOR_MANAGEMENT: 'senior_management',
    AUTHORISED_SIGNATORY: 'authorised_signatory'
  }

  public transactionFilterTerms = {
    CREDIT_ORG_NAME: 'credit_org_name',
    DEBIT_ORG_NAME: 'debit_org_name',
    APPLICATION_ID: 'application_id',
    APPLICANT_ID: 'applicant_id',
    DUE_EVENT: 'due_event',
    EVENT: 'event',
    STATUS: 'status',
    FI_NAME: 'FI_NAME',
    PRODUCT_COMBINATION: 'product_combination'
  };

  public allLendingUserRoles = {
    OPERATIONS: 'Operations',
    CA: 'Credit Analyst',
    CM: 'Credit Manager',
    HOC: 'Head of Credit',
    TSY: 'Treasury',
    LEGAL: 'Legal',
    SALES: 'Sales',
    OM: 'OM',
    CO: 'CO',
    SEO: 'SEO',
    MAKER: 'Maker',
    CHECKER: 'Checker',
    REGULATORY: 'Regulatory',
    TSY2: 'Treasury2',
    FM: 'Finance Manager'
  };

  public financeAppApprovalStatuses = [
    {
      key: 'APPROVED',
      desc: 'Approve'
    },
    {
      key: 'REJECT',
      desc: 'Reject'
    },
    {
      key: 'REQUEST_DOCUMENT',
      desc: 'Request Document'
    },
    {
      key: 'SEND_REWORK',
      desc: 'Send back for rework'
    },
    {
      key: 'REJECT_APPLICATION',
      desc: 'Reject Application'
    }
  ];
  public creditLimitApprovalStatuses = [
    {
      key: 'APPROVED',
      desc: 'Approve'
    },
    {
      key: 'APPROVED_REQUEST',
      desc: 'Approve Request'
    },
    {
      key: 'REJECT',
      desc: 'Reject'
    },
    {
      key: 'REJECT_REQUEST',
      desc: 'Reject Request'
    },
    {
      key: 'REQUEST_DOCUMENT',
      desc: 'Send back to client'
    },
    {
      key: 'BLOCK_CLIENT',
      desc: 'Reject All Facilities'
    },
    {
      key: 'SEND_REWORK',
      desc: 'Send back for rework'
    },
    {
      key: 'SEND_REWORK_TO_OPERATIONS',
      desc: 'Send back to operations'
    },
    {
      key: 'SEND_REWORK_TO_CREDIT',
      desc: 'Send back to credit'
    }
  ];

  public standardSecuritiesOptions = [
    'Undated Security Cheque',
    'Promissory Note',
    'Personal Guarantees',
    'Corporate Guarantees',
    'Hypothecation of Inventory',
    'Hypothecation of Receivables',
    'General Assignment of Receivables',
    'Lien on Fixes Deposits',
    'Other',
  ]

  public documentDetails = [
    { docName: 'Facility Agreement', docDescription: 'Framework Agreement and Master terms and Condition' },
    { docName: 'Corporate Guarantee', docDescription: 'Guarantee provided by a related company for credit facilities of the borrowing company' },
    { docName: 'Personal Guarantee', docDescription: 'Guarantee provided by the shareholders/management for credit facilities of the borrowing company' },
    { docName: 'Assignment of receivable (Specific)', docDescription: 'Specific assignment from buyer (whose invoices will be factored / discounted)' },
    { docName: 'General assignment of receivable', docDescription: 'General assignment of Book debts of the Company' },
    { docName: 'Hypothecation of Inventory', docDescription: 'Hypothecation of Inventory' },
    { docName: 'Original bills of lading', docDescription: 'Document issued by the shipper confirming receipt of goods	 ' },
    { docName: 'Promissory Note', docDescription: '--' },
    { docName: 'Bill of Exchange', docDescription: 'Bill of exchange promising to pay on obligation' },
    { docName: 'Insurance – Stock', docDescription: 'Insurance over general inventory' },
    { docName: 'Insurance – Receivables', docDescription: 'Receivables Insurance specific to approved list of Buyers' },
    { docName: 'Verification Letter', docDescription: '--' },
    { docName: 'Security Cheques and letter', docDescription: 'Undated cheque issued favouring lender for the debt obligation' },
  ];

  termSheetDocumentDetails = [
    { docName: 'Transactional Documents', docDescription: 'Commercial Invoice' },
    { docName: 'Transactional Documents', docDescription: 'Purchase Order' },
    { docName: 'Transactional Documents', docDescription: 'Bill of Lading' },
    { docName: 'Security Documents', docDescription: 'PG of the Signatory and partners' },
    { docName: 'Security Documents', docDescription: 'Hypothecation of inventory and general assignment of receivables ' },
    { docName: 'Security Documents', docDescription: 'Cross-corporate guarantees of sister concern companies' },
    { docName: 'Security Documents', docDescription: 'Updated security cheque of USD <LIMIT>Mn' },
    { docName: 'Security Documents', docDescription: 'Promissory note for USD <LIMIT>Mn' },
    { docName: 'Security Documents', docDescription: '20% Lien in favour of DPWFS Ltd' },
    { docName: 'Security Documents', docDescription: 'MTC' },
    { docName: 'Security Documents', docDescription: 'Notice of assignment' },
  ];

  public creditFacilityRejectionReasons = [
    'Lack of Insurance Coverage',
    'Failed Credit Underwriting Requirements',
    'Poor Financials',
    'Lack of Sufficient Information',
    'Adverse Information',
    'Other'
  ];

  public creditLimitRoleBasedBreadcrum = {
    CA: {
      heading: 'Approve Term Sheet',
      subHeading: 'Review and set term sheet terms'
    },
    SALES: {
      heading: 'Approve Term Sheet',
      subHeading: 'Review and set term sheet terms'
    }
  };

  public draftScreenReferenceMapping = {
    requestCreditFacility: 'APPLY_CREDIT_LIMIT',
    applyFinance: 'APPLY_FINANCE',
    termSheetPublished: 'Term Sheet Published'
  }

  public creditFacilityStatusWiseActiveCards = {
    arranging: {
      'New Facility Request': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 2,
            type: 'edit',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Renew Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 2,
            type: 'edit',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'edit',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'edit',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Financial Institution Assessed': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 5,
            type: 'edit',
            collapse: false
          },
          {
            key: 'history',
            order: 6,
            type: 'view',
            collapse: true
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
        ]
      },
      'Agreement Acceptance': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 5,
            type: 'edit',
            collapse: false
          },
          {
            key: 'history',
            order: 6,
            type: 'view',
            collapse: true
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
        ]
      },
      'View Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 4,
            type: 'edit',
            collapse: false
          },
          {
            key: 'history',
            order: 5,
            type: 'view',
            collapse: true
          },
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'view',
            collapse: false
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
        ]
      },
      'Facility Update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
            enableUpgradeAction: true
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'view',
            collapse: true
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'view',
            collapse: true
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'view',
            collapse: true
          },
          {
            key: 'remarks',
            order: 5,
            type: 'edit',
            collapse: true
          },
          {
            key: 'history',
            order: 6,
            type: 'view',
            collapse: true
          },
        ],
        companyDetails: [
          {
            key: 'companyDocuments',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 2,
            type: 'edit',
            collapse: true
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: true
          },
        ]
      },
      'Documents Required': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
          },
          {
            key: 'remarks',
            order: 2,
            type: 'edit',
            collapse: false
          },
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      }
    },
    lending: {
      'New Facility Request': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 2,
            type: 'edit',
            collapse: false
          }],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 4,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Renew Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 2,
            type: 'edit',
            collapse: false
          }],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'edit',
            collapse: false,
          },
          {
            key: 'companyDocuments',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'edit',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'edit',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Term Sheet Published': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
          },
          {
            key: 'termSheetDocument',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'history',
            order: 4,
            type: 'view',
            collapse: true
          }
        ],
        companyDetails: [
          {
            key: 'companyDocuments',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: true
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Financial Institution Assessed': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'edit',
            collapse: false
          },
          {
            key: 'bankAccountDetails',
            order: 5,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 6,
            type: 'edit',
            collapse: false
          },
          {
            key: 'history',
            order: 7,
            type: 'view',
            collapse: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'View Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
          },
          {
            key: 'termSheetDocument',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'facilityAgreement',
            order: 3,
            type: 'view',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 4,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 5,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 6,
            type: 'edit',
            collapse: false
          },
          {
            key: 'bankAccountDetails',
            order: 7,
            type: 'view',
            collapse: false
          },
          {
            key: 'history',
            order: 8,
            type: 'view',
            collapse: true
          },
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 3,
            type: 'view',
            collapse: false
          },
          {
            key: 'remarks',
            order: 4,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Facility Update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
            enableUpgradeAction: true
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'view',
            collapse: true
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'view',
            collapse: true
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'view',
            collapse: true
          },
          {
            key: 'bankAccountDetails',
            order: 5,
            type: 'view',
            collapse: true
          },
          {
            key: 'remarks',
            order: 6,
            type: 'edit',
            collapse: true
          },
          {
            key: 'history',
            order: 7,
            type: 'view',
            collapse: true
          },
        ],
        companyDetails: [
          {
            key: 'kycInformation',
            order: 1,
            type: 'view',
            collapse: true
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: true
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false,
            enableUpgradeAction: true
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: true
          },
        ]
      },
      'Agreement Accept-limit-update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'edit',
            collapse: false
          },
          {
            key: 'bankAccountDetails',
            order: 5,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 6,
            type: 'edit',
            collapse: false
          },
          {
            key: 'history',
            order: 7,
            type: 'view',
            collapse: false
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
        ]
      },
      'Agreement Accept-Agreement-Change': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'edit',
            collapse: false
          },
          {
            key: 'bankAccountDetails',
            order: 5,
            type: 'edit',
            collapse: false
          },
          {
            key: 'remarks',
            order: 6,
            type: 'edit',
            collapse: false
          },
          {
            key: 'history',
            order: 7,
            type: 'view',
            collapse: false
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
        ]
      },
      'Buyer-Supplier-Update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'view',
            collapse: false
          },
          {
            key: 'bankAccountDetails',
            order: 5,
            type: 'view',
            collapse: false
          },
          {
            key: 'remarks',
            order: 6,
            type: 'view',
            collapse: false
          },
          {
            key: 'history',
            order: 7,
            type: 'view',
            collapse: false
          },
        ],
        companyDetails: [
          {
            key: 'kycInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'edit',
            collapse: false
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'edit',
            collapse: false
          },
        ]
      },
      'Document Change': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'facilityAgreement',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'assignmentOfReceivables',
            order: 3,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierAgreement',
            order: 4,
            type: 'view',
            collapse: false
          },
          {
            key: 'remarks',
            order: 5,
            type: 'view',
            collapse: false
          },
          {
            key: 'history',
            order: 6,
            type: 'view',
            collapse: false
          },
        ],
        companyDetails: [
          {
            key: 'kycInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'edit',
            collapse: false
          },
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'edit',
            collapse: false
          },
        ]
      },
      'Documents Required': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
          },
          {
            key: 'remarks',
            order: 2,
            type: 'edit',
            collapse: false
          },
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'companyDocuments',
            order: 2,
            type: 'edit',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 3,
            type: 'view',
            collapse: true
          },
          {
            key: 'remarks',
            order: 4,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'buyerInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'supplierInformation',
            order: 2,
            type: 'view',
            collapse: false
          },
          {
            key: 'remarks',
            order: 3,
            type: 'edit',
            collapse: false
          }
        ]
      }
    }
  };

  public newCreditFacilityStatusWiseActiveCards = {
    arranging: {
      'New Facility Request': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Renew Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'edit',
            collapse: false,
            enableUpgradeAction: true
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Financial Institution Assessed': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'Agreement Acceptance': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'View Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'Facility Update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
            enableUpgradeAction: true
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false,
            enableUpgradeAction: true
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'Documents Required': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ]
      }
    },
    lending: {
      'New Facility Request': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'edit',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Renew Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'edit',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'edit',
            collapse: false,
            enableUpgradeAction: true
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ]
      },
      'Term Sheet Published': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'Financial Institution Assessed': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        bankDetails: [
          {
            key: 'bankAccountDetails',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'View Page': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        bankDetails: [
          {
            key: 'bankAccountDetails',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'Facility Update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true,
            enableUpgradeAction: true
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false,
            enableUpgradeAction: true
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        bankDetails: [
          {
            key: 'bankAccountDetails',
            order: 1,
            type: 'view',
            collapse: true
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: true
          }
        ]
      },
      'Agreement Accept-limit-update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        bankDetails: [
          {
            key: 'bankAccountDetails',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: false
          }
        ]
      },
      'Agreement Accept-Agreement-Change': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        bankDetails: [
          {
            key: 'bankAccountDetails',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: false
          }
        ]
      },
      'Buyer-Supplier-Update': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        bankDetails: [
          {
            key: 'bankAccountDetails',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: false
          }
        ]
      },
      'Document Change': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: false
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ],
        history: [
          {
            key: 'history',
            order: 1,
            type: 'view',
            collapse: false
          }
        ]
      },
      'Details Required': {
        creditRequirements: [
          {
            key: 'creditLimitTerms',
            order: 1,
            type: 'view',
            collapse: false,
            isApprovedTermsVisible: true
          }
        ],
        companyDetails: [
          {
            key: 'companyInformation',
            order: 1,
            type: 'view',
            collapse: false
          },
          {
            key: 'kycInformation',
            order: 2,
            type: 'view',
            collapse: true
          }
        ],
        counterPartyInformation: [
          {
            key: 'counterPartyInfo',
            order: 1,
            type: 'view',
            collapse: false
          }
        ],
        documents: [
          {
            key: 'documentsTab',
            order: 1,
            type: 'edit',
            collapse: false
          }
        ]
      }
    }
  };

  public cfEligibilityQuestionDesc = {
    net_profit_margin: 'Net profit margin as per last audited financials',
    return_on_equity: 'Return on equity as per last audited financials',
    total_revenue: 'Annual revenue as per last audited financials (million USD)',
    cheque_returns: 'Any check returns in last 12 months',
    history_of_bankruptcy: 'Has company or owners gone through bankruptcy in past'
  };

  public cfEligibilityKeyForQuestion = {
    'Net profit margin as per last audited financials': 'net_profit_margin',
    'Return on equity as per last audited financials': 'return_on_equity',
    'Annual revenue as per last audited financials (million USD)': 'total_revenue',
    'Any check returns in last 12 months': 'cheque_returns',
    'Has company or owners gone through bankruptcy in past': 'history_of_bankruptcy'
  };

  cfCounterPartyLimitUpgradeMetaData = {
    isDisabled: false,
    keyup: { methodCall: 'conditionCheck' },
    fieldType: 'inputType',
    focusout: { methodCall: 'conditionCheck' },
    type: 'text',
    min: 0,
    label: 'Limit',
    value: { methodCall: 'conditionCheck' },
    pattern: { variableName: 'inputPatternWithTwoDecimal' },
    formControlName: "upgradeLimit",
    placeholder: "Upgrade Credit Limit Request Amount",
    autocomplete: "off",
    required: true,
    showError: { methodCall: 'conditionCheck' },
    errorMessage: 'Upgrade credit limit request amount is required.',
    commifiedInput: true,
    inputCondition: 'restrictDecimal'
  };

  cfCounterPartyUsableLimitUpgradeMetaData = {
    isDisabled: false,
    keyup: { methodCall: 'conditionCheck' },
    fieldType: 'inputType',
    focusout: { methodCall: 'conditionCheck' },
    type: 'text',
    min: 0,
    label: 'Usable Limit',
    value: { methodCall: 'conditionCheck' },
    pattern: { variableName: 'inputPatternWithTwoDecimal' },
    formControlName: "usableLimit",
    placeholder: "Usable Limit",
    autocomplete: "off",
    required: true,
    showError: { methodCall: 'conditionCheck' },
    errorMessage: 'Usable Limit is required.',
    commifiedInput: true,
    inputCondition: 'restrictDecimal'
  };

  cfCounterPartyCommodityMetaData = {
    appearance: "outline",
    autocomplete: "off",
    allOptions: { variableName: 'commoditiesList' },
    fieldType: "multiSelectType",
    dropDownValues: { variableName: 'commoditiesListDropDownValues' },
    compareKey: 'name',
    errorMessage: 'require atleast one Commodity',
    focusout: { methodCall: 'conditionCheck' },
    formControlName: 'commodities',
    label: 'Commodities',
    selectedOptions: { variableName: 'selectedCommoditiesList' },
    optionSelected: { methodCall: 'conditionCheck' },
    required: true,
    showError: { methodCall: 'conditionCheck' },
    value: { methodCall: 'conditionCheck' },
    width: 50
  };

  cfRiskParticipationTypeMetaData = {
    isActive: { methodCall: 'conditionCheck' },
    fieldType: 'selectType',
    focusout: { methodCall: 'conditionCheck' },
    label: 'DPWFS share of risk participation Type',
    value: { methodCall: 'conditionCheck' },
    formControlName: "riskParticipationType",
    required: true,
    showError: { methodCall: 'conditionCheck' },
    errorMessage: 'DPWFS share of risk participation type is required.',
    selectionChange: { methodCall: 'conditionCheck' },
    width: 33,
    dropDownValues: { variableName: 'riskParticipationTypeOptions' },
  }

  cfRiskParticipationValueMetaData = {
    isActive: { methodCall: 'conditionCheck' },
    fieldType: 'inputType',
    focusout: { methodCall: 'conditionCheck' },
    type: { methodCall: 'conditionCheck' },
    min: 0,
    max: { methodCall: 'conditionCheck' },
    label: "DPWFS share of risk participation",
    value: { methodCall: 'conditionCheck' },
    formControlName: "riskParticipationValue",
    keyup: { methodCall: 'conditionCheck' },
    placeholder: "DPWFS share of risk participation",
    autocomplete: "off",
    required: true,
    width: 33,
    inputType: { methodCall: 'conditionCheck' },
    showError: { methodCall: 'conditionCheck' },
    errorMessage: 'DPWFS share of risk participation is required.',
  }

  advanceFacilityFields = ['cfRiskParticipationTypeMetaData', 'cfRiskParticipationValueMetaData', 'cfRiskParticipantTypeMetaData', 'cfRiskOriginatorTypeMetaData', 'cfparticipationTypeMetaData'];

  cfRiskParticipantTypeMetaData = {
    isActive: { methodCall: 'conditionCheck' },
    fieldType: 'radioType',
    label: 'Risk Participant *',
    value: { methodCall: 'conditionCheck' },
    formControlName: "riskParticipant",
    required: true,
    showError: { methodCall: 'conditionCheck' },
    errorMessage: 'Risk Participant is required.',
    change: { methodCall: 'conditionCheck' },
    radioOptions: ['DPWFS', 'Participating FI'],
    width: 33,
    radioValues: ['DPWFS', 'PARTICIPATING_FI'],
    doNotChangeOutputvalue: true,
  }

  cfRiskOriginatorTypeMetaData = {
    isActive: { methodCall: 'conditionCheck' },
    fieldType: 'radioType',
    label: 'Risk Originator  *',
    value: { methodCall: 'conditionCheck' },
    formControlName: "riskOriginator",
    required: true,
    showError: { methodCall: 'conditionCheck' },
    width: 33,
    errorMessage: 'Risk Originator is required.',
    change: { methodCall: 'conditionCheck' },
    radioOptions: ['DPWFS', 'Participating FI'],
    radioValues: ['DPWFS', 'PARTICIPATING_FI'],
    doNotChangeOutputvalue: true,
  }

  cfparticipationTypeMetaData = {
    isActive: { methodCall: 'conditionCheck' },
    fieldType: 'radioType',
    label: 'Participation Type *',
    value: { methodCall: 'conditionCheck' },
    width: 33,
    formControlName: "participationType",
    required: true,
    showError: { methodCall: 'conditionCheck' },
    errorMessage: 'Participation Type is required.',
    change: { methodCall: 'conditionCheck' },
    radioOptions: ['Funded ', 'Unfunded'],
    radioValues: ['FUNDED', 'UNFUNDED'],
    doNotChangeOutputvalue: true,
  }

  cfCollectionAtFieldMetaData = {
    isActive: { methodCall: 'conditionCheck' },
    fieldType: 'radioType',
    label: 'Collection At',
    value: { methodCall: 'conditionCheck' },
    width: 33,
    formControlName: "",
    required: true,
    change: { methodCall: 'conditionCheck' },
    radioOptions: ['Upfront', 'On Settlement'],
    radioValues: ['UPFRONT', 'ON_SETTLEMENT'],
    doNotChangeOutputvalue: true,
  }

  cfCollectionFromFieldMetaData = {
    isActive: { methodCall: 'conditionCheck' },
    fieldType: 'radioType',
    label: 'Collection From',
    value: { methodCall: 'conditionCheck' },
    width: 33,
    formControlName: "",
    required: true,
    change: { methodCall: 'conditionCheck' },
    radioOptions: ['Applicant', 'Sponsor'],
    radioValues: ['APPLICANT', 'SPONSOR'],
    doNotChangeOutputvalue: true,
  }


  approvedCfClientUpdateActions = {
    'Add Product': 'requestNewProduct',
    'Update Limit': 'requestLimitUpgrade',
    'Edit Buyer/Supplier': 'editBuyerSupplier',
    'Edit Sectors': 'editSectors',
  };

  approvedCfCAUpdateActions = {
    'Add Product': 'requestNewProduct',
    'Update Limit': 'requestLimitUpgrade',
    'Renew Facility': 'renew',
    'Update Pricing': 'pricingUpdate',
    'Update Usable Limit': 'restrictLimitUsage',
    'Inactivate Facility': 'inactivateFacility',
    'Extend BCC Review Date': 'extendBccReviewDate',
    'Facility Closure': 'facilityClosure',
    'Temporary Enhance': 'temporaryEnhancement',
    'Credit Facility Extension': 'creditFacilityExtension'
  };

  updateFlowRequestMapping = {
    requestLimitUpgrade: 'LIMIT',
    requestNewProduct: 'PRODUCT',
    renew: 'BCC_RENEW',
    pricingUpdate: 'PRICING',
    inactivateFacility: 'INACTIVATION',
    restrictLimitUsage: 'USABLE_LIMIT',
    extendBccReviewDate: 'EXTEND_BCC_REVIEW_DATE',
    facilityClosure: 'FACILITY_CLOSURE',
    temporaryEnhancement: 'TEMPORARY_ENHANCEMENT',
    creditFacilityExtension: 'EXTENSION',
    editBuyerSupplier: 'BUYER'
  };

  creditUpdateWorkflows = ['CREDIT_RENEW', 'CREDIT_UPDATE', 'CREDIT_UPDATE_BUYER', 'CREDIT_EXTEND_BCC_REVIEW_DATE', 'CREDIT_RENEW_TERMS', 'CREDIT_UPDATE_TERMS', 'CREDIT_UPDATE_TERMS_ASSESSMENT', 'FACILITY_CLOSURE', 'CREDIT_UPDATE_INSURANCE_REVIEW', 'CREDIT_RENEW_INSURANCE_REVIEW'];
  newDocUploadStatuses = ['Limit Update Under Assessment', 'Product Addition Under Assessment', 'CounterParty Edit Under Assessment']
  creditUpdateResponseStates = {
    CREDIT_UPDATE: {
      CLIENT: ['Limit Update Details Required', 'Product Update Details Required'],
      CA: ['Limit Update Under Assessment', 'Limit Update Rework Pending by Credit Analyst', 'Product Addition Under Assessment', 'Product Update Rework Pending by Credit Analyst',
        'Limit Update Reviewed By Credit Analyst', 'Product Update Rework Pending by Credit Analyst', 'CounterParty Edit Rework Pending by Credit Analyst'],
      CM: ['Limit Update Reviewed By Credit Analyst', 'Product Update Reviewed By Credit Analyst'],
      HOC: ['Limit Update Reviewed By Credit Analyst', 'Limit Update Reviewed By Credit Manager', 'Product Update Reviewed By Credit Analyst', 'Product Update Reviewed By Credit Manager']
    },
    CREDIT_RENEW: {
      CLIENT: ['Limit Update Details Required', 'Product Update Details Required'],
      CA: ['Limit Update Under Assessment', 'Limit Update Rework Pending by Credit Analyst', 'Product Addition Under Assessment', 'Product Update Rework Pending by Credit Analyst',
        'Limit Update Reviewed By Credit Analyst', 'Product Update Rework Pending by Credit Analyst', 'CounterParty Edit Rework Pending by Credit Analyst'],
      CM: ['Limit Update Reviewed By Credit Analyst', 'Product Update Reviewed By Credit Analyst'],
      HOC: ['Limit Update Reviewed By Credit Analyst', 'Limit Update Reviewed By Credit Manager', 'Product Update Reviewed By Credit Analyst', 'Product Update Reviewed By Credit Manager']
    },
    CREDIT_UPDATE_TERMS: {
      CA: ['Limit Update Approved By Credit Team', 'Product Update Approved By Credit Team'],
      //add new status here
      MAKER: ['Limit Update Approved By Credit Team', 'Product Update Approved By Credit Team'],
      //add new status here
      CHECKER: ['Limit Update Loan Documents Uploaded', 'Product Update Loan Documents Uploaded'],
      LEGAL: ['Limit Update Loan Documents Review Pending', 'Product Update Loan Documents Review Pending', 'Agreement Change Reviewed by Credit Analyst'],
      CLIENT: ['Limit Update Terms Accepted', 'Product Update Terms Accepted', 'Agreement Change Terms Accepted']
    },
    CREDIT_RENEW_TERMS: {
      CA: ['Limit Update Approved By Credit Team', 'Product Update Approved By Credit Team'],
      //add new status here
      MAKER: ['Limit Update Approved By Credit Team', 'Product Update Approved By Credit Team'],
      //add new status here
      CHECKER: ['Limit Update Loan Documents Uploaded', 'Product Update Loan Documents Uploaded'],
      LEGAL: ['Limit Update Loan Documents Review Pending', 'Product Update Loan Documents Review Pending', 'Agreement Change Reviewed by Credit Analyst'],
      CLIENT: ['Limit Update Terms Accepted', 'Product Update Terms Accepted', 'Agreement Change Terms Accepted']
    },
    CREDIT_UPDATE_TERMS_ASSESSMENT: {
      OPERATIONS: ['Limit Update Terms Accepted', 'Product Update Terms Accepted', 'Agreement Change Terms Accepted', 'Limit Update Rework Pending by Operations', 'Product Update Rework Pending by Operations', 'Agreement Change Rework Pending by Operations', 'Renew Rework Pending by Operations', 'Facility Update Terms Accepted', 'Temporary Enhancement Update Terms Accepted', 'Temporary Enhancement Update Rework Pending by Operations'],
      CA: ['Limit Update Facility Documents Reviewed By Operations', 'Product Update Facility Documents Reviewed By Operations', 'Agreement Change Documents Reviewed By Operations', 'Renew Facility Documents Reviewed By Operations', 'Facility Update Facility Documents Reviewed By Operations', 'Facility Update Terms Accepted', 'Temporary Enhancement Update Facility Documents Reviewed By Operations'],
      FM: ['Limit Update Facility Documents Reviewed By Operations & Credit', 'Product Update Facility Documents Reviewed By Operations & Credit', 'Agreement Change Documents Reviewed By Operations & Credit', 'Renew Facility Documents Reviewed By Operations & Credit', 'Facility Update Facility Documents Reviewed By Operations & Credit', 'Temporary Enhancement Update Facility Documents Reviewed By Operations & Credit', 'Limit Update Facility Documents Reviewed By Operations', 'Product Update Facility Documents Reviewed By Operations', 'Agreement Change Documents Reviewed By Operations', 'Renew Facility Documents Reviewed By Operations', 'Facility Update Facility Documents Reviewed By Operations', 'Temporary Enhancement Update Facility Documents Reviewed By Operations'],
      LEGAL: ['Limit Update Facility Documents Reviewed By Operations, Credit & FM', 'Product Update Facility Documents Reviewed By Operations, Credit & FM', 'Agreement Change Documents Reviewed By Operations, Credit & FM', 'Renew Facility Documents Reviewed By Operations, Credit & FM', 'Facility Update Facility Documents Reviewed By Operations, Credit & FM', 'Temporary Enhancement Update Facility Documents Reviewed By Operations, Credit & FM', 'Limit Update Facility Documents Reviewed By Operations', 'Product Update Facility Documents Reviewed By Operations', 'Agreement Change Documents Reviewed By Operations', 'Renew Facility Documents Reviewed By Operations', 'Facility Update Facility Documents Reviewed By Operations', 'Limit Update Facility Documents Reviewed By Operations & Credit', 'Product Update Facility Documents Reviewed By Operations & Credit', 'Agreement Change Documents Reviewed By Operations & Credit', 'Renew Facility Documents Reviewed By Operations & Credit', 'Facility Update Facility Documents Reviewed By Operations & Credit', 'Facility Update Terms Accepted', 'Temporary Enhancement Update Facility Documents Reviewed By Operations', 'Temporary Enhancement Update Facility Documents Reviewed By Operations & Credit'],
      CLIENT: ['Limit Update Signature Pending', 'Product Update Signature Pending', 'Agreement Change Signature Pending', 'Facility Update Signature Pending', 'Temporary Enhancement Update Signature Pending'],
    },
    CREDIT_RENEW_TERMS_ASSESSMENT: {
      OPERATIONS: ['Limit Update Terms Accepted', 'Product Update Terms Accepted', 'Agreement Change Terms Accepted', 'Limit Update Rework Pending by Operations', 'Product Update Rework Pending by Operations', 'Agreement Change Rework Pending by Operations', 'Renew Rework Pending by Operations', 'Facility Update Terms Accepted'],
      CA: ['Limit Update Facility Documents Reviewed By Operations', 'Product Update Facility Documents Reviewed By Operations', 'Agreement Change Documents Reviewed By Operations', 'Renew Facility Documents Reviewed By Operations', 'Facility Update Terms Accepted'],
      FM: ['Renew Facility Documents Reviewed By Operations & Credit', 'Limit Update Facility Documents Reviewed By Operations & Credit', 'Product Update Facility Documents Reviewed By Operations & Credit', 'Agreement Change Documents Reviewed By Operations & Credit', 'Renew Facility Documents Reviewed By Operations', 'Limit Update Facility Documents Reviewed By Operations', 'Product Update Facility Documents Reviewed By Operations', 'Agreement Change Documents Reviewed By Operations'],
      LEGAL: ['Renew Facility Documents Reviewed By Operations, Credit & FM', 'Limit Update Facility Documents Reviewed By Operations, Credit & FM', 'Product Update Facility Documents Reviewed By Operations, Credit & FM', 'Agreement Change Documents Reviewed By Operations, Credit & FM', 'Limit Update Facility Documents Reviewed By Operations', 'Product Update Facility Documents Reviewed By Operations', 'Agreement Change Documents Reviewed By Operations', 'Renew Facility Documents Reviewed By Operations', 'Limit Update Facility Documents Reviewed By Operations & Credit', 'Product Update Facility Documents Reviewed By Operations & Credit', 'Agreement Change Documents Reviewed By Operations & Credit', 'Renew Facility Documents Reviewed By Operations & Credit', 'Facility Update Terms Accepted'],
      CLIENT: ['Limit Update Signature Pending', 'Product Update Signature Pending', 'Agreement Change Signature Pending'],
    },
    CREDIT_UPDATE_BUYER: {
      CLIENT: ['CounterParty Edit Details Required', 'CounterParty Edit Under Assessment'],
      CA: ['CounterParty Edit Under Assessment', 'CounterParty Edit Rework Pending by Credit Analyst'],
      HOC: ['CounterParty Edit Reviewed by Credit Analyst']
    },
    CREDIT_EXTEND_BCC_REVIEW_DATE: {
      CA: ['BCC Review Extension Rework Pending by Credit Analyst'],
      HOC: ['BCC Review Extension Reviewed by Credit Analyst']
    },
    CREDIT_RENEW_INSURANCE_REVIEW: {
      CA: ['Insurance Review Pending'],
    },
    CREDIT_UPDATE_INSURANCE_REVIEW: {
      CA: ['Insurance Review Pending'],
    }
  }


  public allCfUpdateArrangingFIStatus = [
    'Limit Update Under Assessment',
    'Limit Update Financial Institution Assessed',
    'Limit Update Rejected by Financial Institution',
    'Limit Update Rejected by Applicant',
    'Product Update Under Assessment',
    'Sector Update Under Assessment',
    'Product Update Financial Institution Assessed',
    'Product Update Rejected by Financial Institution',
    'Product Update Rejected by Applicant',
    'CounterParty Edit Under Assessment',
    'CounterParty Edit Financial Institution Assessed',
    'CounterParty Edit Rejected by Financial Institution',
    'CounterParty Edit Rejected by Applicant',
    'Facility Update Financial Institution Assessed',
    'Temporary Enhancement Update Financial Institution Assessed'
  ];

  public cfArrangingFIUpdateEnabledStatuses = [
    'Under Assessment',
    'Limit Update Under Assessment',
    'Product Update Under Assessment',
    'Sector Update Under Assessment',
    'CounterParty Edit Under Assessment',
    'Eligibility Requested',
    'Renew Under Assessment'
  ];

  public cfArrangingClientUpdateEnabledStatuses = [
    'Financial Institution Assessed',
    'Limit Update Financial Institution Assessed',
    'Product Update Financial Institution Assessed',
    'CounterParty Edit Financial Institution Assessed',
    'Agreement Change Financial Institution Assessed',
    'Renew Financial Institution Assessed',
    'Facility Update Financial Institution Assessed',
    'Temporary Enhancement Update Financial Institution Assessed'
  ];

  public cpIfBulkUploadHeadersFormat = {
    'Company Name': 'name',
    'Country': 'country',
    'Commodities': 'commodities',
    'Limit': 'limit',
    'Payment Terms': 'paymentTerms',
    'Business Vintage (In Months)': 'businessVintage',
    'Percentage Of Purchase': 'percentageSalesPurchases',
    'Related Party': 'relatedParty',
    'Name': 'organisation_name',
    'Country Code': 'country_code',
    'Contact Number': 'contact_number',
    'Email ID': 'email',
    'Designation': 'designation',
    'State/Province': 'state',
    'City': 'city',
    'Pin Box/PO Box/Zip Code': 'pin_code',
    'Address': 'address_line',
    'Countries importer is dealing in': 'countries_operate_in',
    'How long has the importer been dealing in these commodities?': 'years_bco_dealing_with_commodity',
    'How old is the relationship with importer?': 'years_of_relationship_with_bco',
    'Payment history with importer': 'default_in_past_payments',
    'Annual Turn-over of importer': 'annual_turnover',
    'Additional Comments': 'comments',
    'Annual Business Volumne': 'annualBusinessVolume',
    'Country Business Type': 'companyBusinessType',
    'Passport Issuing Country Code': 'passportIssuingCountryCode',
    'Registered in UAE Free Trade Zone': 'uaeFreeTradeZoneRegistered',
    'VAT Number or TIN/TRN Number': 'taxIdentificationNumber',
    'Trade/Commercial Licence Number': 'licenceNumber',
    'Peppol Id': 'peppolId'
  };

  public cpIdRfTpBulkUploadHeadersFormat = {
    'Company Name': 'name',
    'Country': 'country',
    'Commodities': 'commodities',
    'Payment Terms': 'paymentTerms',
    'Business Vintage (In Months)': 'businessVintage',
    'Annual Business Volumne': 'annualBusinessVolume',
    'Percentage Of Purchase': 'percentageSalesPurchases',
    'Country Business Type': 'companyBusinessType',
    'Passport Issuing Country Code': 'passportIssuingCountryCode',
    'Registered in UAE Free Trade Zone': 'uaeFreeTradeZoneRegistered',
    'VAT Number or TIN/TRN Number': 'taxIdentificationNumber',
    'Trade/Commercial Licence Number': 'licenceNumber',
    'Peppol Id': 'peppolId',
    'Related Party': 'relatedParty',
    'State/Province': 'state',
    'City': 'city',
    'Address': 'address_line',
    'Limit': 'limit'
  };

  public cpBulkUploadErrorHandler = {
    INTERNAL_SERVER_ERROR: 'Error occured while bulk updating Counter Party data',
    PARTIAL_DATA_UPLOADED: 'Error in updating few records. Please check error files for details and upload those records again post correction.',
    SUCCESSFUL_DATA_UPLOAD: 'All records are updated',
    EMPTY_FILE_UPLOADED: 'Empty file uploaded, Please check "File Format".',
    WRONG_FORMAT_FILE_UPLOADED: 'File with wrong format uploaded, Please check "File Format".',
    MULTIPLE_FILE_UPLOAD: 'Cannot upload multiple files',
    COUNTRY_MISSING: 'Country',
    COUNTRY_INVALID: 'Country',
    COUNTRY_INVALID_COUNTRIES_OPERATE_IN: 'Countries importer is dealing in',
    COUNTRY_INVALID_PASSPORT_ISSUING_COUNTRY_CODE: 'Passport Issuing Country Code',
    COMPANY_NAME: 'Company name',
    MANDATORY_DATA_MISSING: 'Mandatory fields missing.',
    COUNTRY_CODE_MISSING: 'Country Code',
    COUNTRY_CODE_INVALID: 'Country Code',
    STATE_MISSING: 'State',
    STATE_INVALID: 'State',
    CITY_MISSING: 'City',
    CONTACT_NUMBER_INVALID: 'Contact number',
    EMAIL_INVALID: 'Email',
    INVALID_VALUE: 'INVALID value in column: ',
    INVALID_VALUE_COMPANY_BUSINESS_TYPE: 'Country Business Type',
    INVALID_VALUE_UAE_FREE_TRADE_ZONE: 'Registered in UAE Free Trade Zone',
    INVALID_VALUE_YEARS_BCO_DEALING: 'How long has the importer been dealing in these commodities?',
    INVALID_VALUE_YEARS_OF_RELATIONSHIP: 'How old is the relationship with importer?',
    INVALID_VALUE_DEFAULT_IN_PAST_PAYMENTS: 'Payment history with importer',
    INVALID_VALUE_ANNUAL_TURNOVER: 'Annual Turn-over of importer',
    COMMODITY_INVALID: 'Commodity',
    PAYMENT_TERMS_MISSING: 'Payment Terms',
    BUSINESS_VINTAGE_MISSING: 'Business Vintage',
    PERCENTAGE_OF_PURCHASE_MISSING: 'Percentage Of Purchase',
    COMMODITY_MISSING: 'Commodity',
    RELATED_PARTY_MISSING: 'Related party'
  };

  public cfDocumentsRequiredStatuses = [
    'Details Required',
    'Limit Update Details Required',
    'Product Update Details Required',
    'CounterParty Edit Details Required',
    'Renew Details Required',
    'Sector Update Documents Required',
    'Documents Required',
    'CounterParty Edit Under Assessment'
  ];

  public cfDetailsRequiredStatuses = [
    'Details Required',
    'Limit Update Details Required',
    'Product Update Details Required',
    'CounterParty Edit Details Required',
    'Renew Details Required',
    'CounterParty Edit Under Assessment'
  ];

  public companyRegistrationExtraParams = ['companyTaxDetails', 'companyAttributesDetails', 'channel', 'source', 'leadId', 'opportunityId', 'agreementDetails', 'clientAgreementSignedDate', 'companyOwner', 'approvedOn', 'salesAgent', 'salesAgentEmail', 'parentCompanyCode', 'log_level_name'];

  public cfAllUpdateStatus = [
    'Limit Update Reviewed By Credit Analyst',
    'Product Update Reviewed By Credit Analyst',
    'Product Update Under Assessment',
    'Sector Update Under Assessment',
    'Limit Update Details Required',
    'Product Update Details Required',
    'Limit Update Rejected by Financial Institution',
    'Product Update Rejected by Financial Institution',
    'Limit Update Reviewed By $active_department',
    'Limit Update Approved By Credit Team',
    'Product Update Approved By Credit Team',
    'Limit Update Rework Pending by Credit Analyst',
    'Product Update Rework Pending by Credit Analyst',
    'Limit Update Rejected by Applicant',
    'Product Update Rejected by Applicant',
    'Limit Update Under Assessment',
    'Product Addition Under Assessment',
    'CounterParty Edit Reviewed by Credit Analyst',
    'CounterParty Edit Details Required',
    'CounterParty Edit Rejected by Financial Institution',
    'CounterParty Edit Reviewed by $active_department',
    'CounterParty Edit Rework Pending by Credit Analyst',
    'CounterParty Edit Rejected by Financial Institution',
    'CounterParty Edit Rejected by Applicant',
    'CounterParty Edit Under Assessment',
    'Limit Update Loan Documents Review Pending',
    'Product Update Loan Documents Review Pending',
    'Agreement Change Reviewed by Credit Analyst',
    'Agreement Change Rejected by Financial Institution',
    'Limit Update Terms Accepted',
    'Product Update Terms Accepted',
    'Agreement Change Terms Accepted',
    'Limit Update Document Rework Pending',
    'Product Update Document Rework Pending',
    'Agreement Change Document Rework Pending',
    'Renew Loan Documents Uploaded',
    'Limit Update Loan Documents Uploaded',
    'Product Update Loan Documents Uploaded',
    //add new status here
    'Limit Update Approved By Credit Team',
    'CounterParty Edit Approved By Credit Team',
    'Product Addition Approved By Credit Team',
    'Limit Update Facility Documents Reviewed By Operations',
    'Product Update Facility Documents Reviewed By Operations',
    'Agreement Change Documents Reviewed By Operations',
    'Limit Update Facility Documents Reviewed By Operations & Credit',
    'Product Update Facility Documents Reviewed By Operations & Credit',
    'Agreement Change Documents Reviewed By Operations & Credit',
    'Limit Update Signature Pending',
    'Product Update Signature Pending',
    'Agreement Change Signature Pending',
    'Facility Update Signature Pending',
    'Limit Update Rework Pending by Operations',
    'Product Update Rework Pending by Operations',
    'Agreement Change Rework Pending by Operations',
    'Product Update Loan Documents Issued',
    'Renew Loan Documents Issued',
    'Agreement Change Loan Documents Issued',
    'Limit Update Loan Documents Issued',
    'Renew Signature Pending',
    'Renew Facility Documents Reviewed By Operations',
    'Renew Facility Documents Reviewed By Operations & Credit',
    'Renew Rework Pending by Operations',
    'Renew Under Assessment',
    'Renew Reviewed By Credit Analyst',
    'Renew Approved By Credit Team',
    'Renew Loan Documents Review Pending',
    'Renew Rejected',
    'Renew Rejected By Applicant',
    'Renew Rejected By Financial Institution',
    'Renew Rejected By Counter Party',
    'Renew Documents Pending',
    'Renew Importer Acceptance Pending',
    'Renew Financial Institution Assessed',
    'Renew Terms Accepted',
    'Renew Details Required',
    'Renew Reviewed By Head Of Credit',
    'Facility Update Financial Institution Assessed',
    'Temporary Enhancement Update Financial Institution Assessed',
    'Facility Inactivation Reviewed by Credit Analyst',
    'Facility Inactivation Rework Pending by Credit Analyst',
    'Facility Inactivated',
    'Expiry Extension Reviewed by Credit Analyst',
    'Expiry Extension Rework Pending by Credit Analyst',
    'Usable Limit Update Reviewed by Credit Analyst',
    'Usable Limit Update Rework Pending by Credit Analyst',
    'Facility Update Under Assessment',
    'Facility Update Reviewed By Credit Analyst',
    'Facility Update Documents Required',
    'Facility Update Rework Pending by Credit Analyst',
    'Facility Update Approved By Credit Team',
    'Facility Update Loan Documents Review Pending',
    'Facility Update Loan Documents Issued',
    'Facility Update Loan Documents Uploaded',
    'Facility Update Terms Accepted',
    'Facility Update Document Rework Pending',
    'Facility Update Facility Documents Reviewed By Operations',
    'Facility Update Signature Pending',
    'Facility Update Rework Pending by Operations',
    'Facility Update Rejected by Financial Institution',
    'Facility Update Rejected by Applicant',
    'Facility Update Facility Documents Reviewed By Operations & Credit',
    'Facility Update Financial Institution Assessed',
    'Temporary Enhancement Update Financial Institution Assessed',
    'Sector Update Under Assessment',
    'Sector Update Reviewed By Credit Analyst',
    'Sector Update Documents Required',
    'Sector Update Rework Pending by Credit Analyst',
    'Sector Update Approved By Credit Team',
    'Facility Closure Under Assessment',
    'Facility Closure Reviewed by Operations',
    'Facility Closure Reviewed by Credit Analyst',
    'Facility Closure Reviewed By Head Of Credit',
    'Facility Closure Reviewed By Maker',
    'Facility Closure Rework Pending by Maker',
  ];

  public cfSignaturePendingStatuses = [
    'Renew Signature Pending',
    'Limit Update Signature Pending',
    'CounterParty Edit Signature Pending',
    'Product Update Signature Pending',
    'Agreement Change Signature Pending',
    'Signature Pending',
    'Facility Update Signature Pending',
  ];

  public SCFTemplateDocumentIdcayman = {
    development: '1063315',
    qa: '1063315',
    Staging: '1082925',
    Uat: 'UAT_TODO',
    Demo: '1004431',
    Prod: '1004273',
    Dev: '1063315'// change ID on the basis of environment
  }

  public SCFTemplateDocumentIdDIFC = {
    development: '1060916',
    qa: '1065685',
    Staging: '1084914',
    Uat: 'UAT_TODO',
    Demo: '1004432',
    Prod: '1004467',
    Dev: '1060916'// change ID on the basis of environment
  }

  public additionalRejectStatuses = [
    'Limit Update Rejected by Financial Institution',
    'Limit Update Rejected by Applicant',
    'Product Update Rejected by Financial Institution',
    'Product Update Rejected by Applicant',
    'CounterParty Edit Rejected by Financial Institution',
    'CounterParty Edit Rejected by Applicant',
    'Agreement Change Rejected by Financial Institution',
    'Renew Rejected',
    'Renew Rejected By Applicant',
    'Renew Rejected By Financial Institution',
    'Renew Rejected By Counter Party',
  ];

  public SCFSupplierBulkUploadTemplateDocumentIdcayman = {
    development: '1059962',
    qa: '1121187',
    Staging: '1387303',
    Uat: 'UAT_TODO',
    Demo: '1004430',
    Prod: '1121389',
    Dev: '1121187'// change ID on the basis of environment
  }

  public SCFSupplierBulkUploadTemplateDocumentIdDIFC = {
    development: '1060926',
    qa: '1065697',
    Staging: '1387302',
    Uat: 'UAT_TODO',
    Demo: '1004434',
    Prod: '1121389',
    Dev: '1065697'// change ID on the basis of environment
  }

  public SCFInvoiceBulkUploadTemplateDocumentIdcayman = {
    development: '1062470',
    qa: '1072678',
    Staging: '1101049',
    Uat: 'UAT_TODO',
    Demo: '1004501',
    Prod: '1005917',
    Dev: '1062470'// change ID on the basis of environment
  }

  public SCFInvoiceBulkUploadTemplateDocumentIdDIFC = {
    development: '1062471',
    qa: '1072693',
    Staging: '1101087',
    Uat: 'UAT_TODO',
    Demo: '1004502',
    Prod: '1005916',
    Dev: '1062471'// change ID on the basis of environment
  }

  public scfInvoicesBulkUploadHeadersFormat = {
    'Finance Application No': 'finance_id',
    'Supplier Reference No': 'supplier_id',
    'Invoice No': 'invoice_number',
    'Disbursement Date (dd/mm/yyyy)': 'disbursement_date',
    'Actual Settlement Date (dd/mm/yyyy)': 'actual_settlement_date',
    'Status': 'status',
    'Interest Charges': 'interestCharges'
  };

  public scfInvoicesBulkUploadkeyToHeaderName = {
    'finance_id': 'Finance Application No',
    'supplier_id': 'Supplier Reference No',
    'invoice_number': 'Invoice No',
    'disbursement_date': 'Disbursement Date (dd/mm/yyyy)',
    'actual_settlement_date': 'Actual Settlement Date (dd/mm/yyyy)',
    'status': 'Status',
    'interestCharges': 'Interest Charges'
  };


  public scfSupplierBulkUploadHeadersFormat = {
    'Action': 'action',
    'Supplier Name': 'name',
    'Supplier Country': 'country',
    'Supplier Account Number': 'account_number',
    'Supplier Category': 'category',
    'Supplier Ref No': 'bp_number',
  };

  public compRegUpdateAPIFormat = {
    statusCode: { 'name': {}, 'description': {} },
    tenant: { 'name': {}, 'description': {} },
    createdBy: {},
    createCompanyStatus: { any: {} },
    createdDate: {},
    lastModifiedBy: {},
    lastModifiedDate: {},
    companyName: {},
    countryCode: {},
    stateName: {},
    taxApplicable: {},
    taxNumber: {},
    addressLine1: {},
    addressLine2: {},
    cityName: {},
    postalCode: {},
    companyEmail: {},
    contactNumberCountryCode: {},
    contactNumber: {},
    companyId: {},
    companyReference: {},
    companyRequestId: {},
    companyRegisteredUnder: { any: {} },
    tradersCode: {},
    companyCode: {},
    companyType: {},
    businessType: {},
    parentCompanyReference: {},
    parentCompanyId: {},
    isSuspended: {},
    ownedBy: {},
    taxRegistrationNumber: '',
    directShareholderDetails: { companyCodeIssuerRequestId: {}, id: {}, name: {}, nationality: {}, parentId: {}, pepFlag: {}, percentage: {}, relatedTo: {} },
    companyOfficeDetails: {
      'taxRegistrationNumber': {}, 'stateName': {}, 'address': {}, 'id': {}, 'isVisible': {}, 'companyId': {}, 'companyRequestId': {}, 'companyTaxRequestId': {}, 'taxType': {},
      'officeReference': {}, 'officeName': {}, 'officeType': {}, 'cityName': {}, 'companyOfficeRequestId': {}, 'postalCode': {}, 'isGstAddress': {}, 'companyOfficeAttributesDetails': {}, 'createdBy': {},
      'createdDate': {}, 'lastModifiedBy': {}, 'lastModifiedDate': {}, 'statusCode': { 'name': {}, 'description': {} }
    },
    companyPaymentDetails: { any: {} },
    offices: { any: {} },
    companyDocumentDetails: {
      'isItExtraDoc': {}, 'expiryDate': {}, 'createdBy': {}, 'createdDate': {}, 'lastModifiedBy': {}, 'lastModifiedDate': {}, 'statusCode': { 'name': {}, 'description': {} }, 'documentName': {}, 'documentNumber': {}, 'documentReference': {},
      'documentType': {}, 'documentCategory': {}, 'companyId': {}, 'id': {}, 'companyRequestId': {}, 'companyDocumentRequestId': {}, 'issueDate': {}
    },
    companyCodeIssuerDetails: {
      'identifierCode': {}, 'identifierCodeType': {}, 'companyCodeIssuerRequestId': {}, 'companyId': {}, 'companyRequestId': {}, 'id': {}, 'correspondingCategory': {}, 'correspondingCategoryValue': {},
      'createdBy': {}, 'createdDate': {}, 'identifierIssuedBy': {}, 'identifierValue': {}, 'issuerCategory': {}, 'issuerCategoryValue': {}, 'lastModifiedBy': {}, 'lastModifiedDate': {}, 'statusCode': { 'name': {}, 'description': {} }
    },
    companyAdditionalInfoDetails: {
      'id': {}, 'companyId': {}, 'createdBy': {}, 'createdDate': {}, 'lastModifiedBy': {}, 'lastModifiedDate': {}, 'companyRequestId': {}, 'companyAdditionalInfoId': {}, 'companyAdditionalInfoRequestId': {},
      'clientTypeCode': {}, 'serviceProviderCode': {}, 'countryCode': {}, 'questionCode': {}, 'statusCode': { 'name': {}, 'description': {} }, 'companyAdditionalInfoMapperDetails': {
        'id': {}, 'companyAdditionalInfoId': {}, 'companyAdditionalInfoMapperRequestId': {}, 'companyAdditionalInfoRequestId': {}, 'answer': {}, 'createdBy': {}, 'createdDate': {}, 'lastModifiedBy': {},
        'lastModifiedDate': {}, 'statusCode': { 'name': {}, 'description': {} }
      }
    },
    companyAttributesDetails: {
      'statusCode': { 'name': {}, 'description': {} },
      'attributeNameAttributeValuePair': { 'key': {}, 'value': {} }, id: {}
    },
    companyClientTypes: {
      'clientTypeCode': {}, 'clientTypeName': {}, 'companyClientTypeRequestId': {}, 'companyClientTypeId': {},
      'companyServiceProviderDetails': {
        'locationCode': {}, 'locationName': {}, 'serviceProviderCode': {}, 'serviceProviderName': {}, 'statusCode': { 'name': {}, 'description': {} }, 'active': {}, 'createdBy': {}, 'createdDate': {}, 'lastModifiedBy': {}, 'lastModifiedDate': {},
        'id': {}, 'companyClientTypeId': {}, 'companyServiceProviderRequestId': {}, 'companyClientTypeRequestId': {}, 'workflowId': {}, 'workflowStatus': {}, 'companyListedServiceDetails': {
          'statusCode': { 'name': {}, 'description': {} }, 'createdBy': {}, 'createdDate': {}, 'lastModifiedBy': {}, 'lastModifiedDate': {}, 'id': {}, 'companyServiceProviderId': {}, 'companyListedServiceRequestId': {},
          'companyServiceProviderRequestId': {}, 'serviceCode': {}
        }
      }, 'companyId': {}, 'statusCode': { 'name': {}, 'description': {} }, 'createdBy': {}, 'createdDate': {},
      'lastModifiedBy': {}, 'lastModifiedDate': {}, 'id': {}, 'companyRequestId': {}
    },
    sponsorIds: {},
    deletedSponsorIds: {},
    version: ''
  };
  public invoicesBulkUploadErrorHandler = {
    EMPTY_FILE_UPLOADED: 'Empty file uploaded, Please check "File Format".',
    WRONG_FORMAT_FILE_UPLOADED: 'File with wrong format uploaded, Please check "File Format".',
    MANDATORY_DATA_MISSING: 'Mandatory column missing: ',
    INVALID_STATUS_PRESENT: 'Invalid status present',
    INVALID_DISBURSAL_DATE: 'Invalid disbursal date'
  };

  public BankReceiptsErrorMessageMapping = {
    'INVALID_CLIENT_REFERENCE': 'Reference number is not valid for this client. Please remove this reference number to proceed.',
    'INVALID_UTILISATION_ZERO': 'Receipt balance amount is 0. Please remove this reference number to proceed.',
    'INVALID_UTILISATION_NON_ZERO': 'Margin Money to be remitted is more than receipt balance amount.'
  }

  public ourEnvToNotificationServiceOriginEnvMapping = {
    development: 'development',
    qa: 'qa',
    Staging: 'staging',
    Uat: 'UAT_TODO',
    Demo: 'demo',
    Prod: 'production',
    Dev: 'development'
  }

  public static documentType = [
    { value: 'TermsAndCondition', viewValue: 'Terms & Conditions' },
    { value: 'FIStandardTerms', viewValue: 'Fi Standard Terms' },
    { value: 'ClientAgreement', viewValue: 'Client Agreement' },
    { value: 'PrivacyPolicy', viewValue: 'Privacy Policy' },
    { value: 'PlatformDisclaimer', viewValue: 'Platform Disclaimer' },
    { value: 'ClientClassificationNotice', viewValue: 'Client Classification Notice' },
    { value: 'ClientClassificationNoticeForFI', viewValue: 'FI Classification Notice' }

  ];

  public static facilityDocumentTypeLabelMap: { [key: string]: string } = {
    // Existing mappings
    'accept_term_sheet': 'Accept Term Sheet',
    'account_category_update_document': 'Account Category Update Document',
    'AFAR': 'Audited Financials + Annual Report',
    'aff_invoice': 'AFF Invoice',
    'airway_bill': 'Airway Bill',
    'AP': 'Address Proof',
    'ARR': 'Arrangement Document',
    'assignment_of_receivables': 'Assignment of Receivables',
    'bank_approval_document': 'Bank Approval Document',
    'BS': 'Balance Sheet',
    'CBR': 'Credit Bureau Report',
    'certificate_of_origin': 'Certificate of Origin',
    'CI': 'Certificate of Incorporation',
    'commercial_invoice': 'Commercial Invoice',
    'counter_party_document': 'Counter Party Document',
    'counterparty_invoice': 'Past Invoice',
    'counterparty_trade_license': 'Trade License',
    'CSC': 'Ownership Structure Chart',
    'delivery_note': 'Delivery Note',
    'DOS': 'Details of Shareholders and UBOs',
    'ECR': 'Export Credit Report',
    'finacialDecisioningDocument': 'Financial Decisioning Document',
    'FACILITY_AGREEMENT:CORPORATE_GUARANTEE': 'Facility Agreement - Corporate Guarantee',
    'FACILITY_AGREEMENT:INVENTORY_FINANCE_BUYER': 'Facility Agreement - Inventory Finance Buyer',
    'FACILITY_AGREEMENT:INVOICE_DISCOUNTING': 'Facility Agreement - Invoice Discounting',
    'FACILITY_AGREEMENT:INVOICE_FACTORING': 'Facility Agreement - Invoice Factoring',
    'FACILITY_AGREEMENT:INVOICE_FACTORING_WO_RECOURSE': 'Facility Agreement - Invoice Factoring Without Recourse',
    'FACILITY_AGREEMENT:MASTER': 'Facility Agreement - Master',
    'FACILITY_AGREEMENT:MRPA': 'Facility Agreement - MRPA',
    'FACILITY_AGREEMENT:OTHER': 'Facility Agreement - Other',
    'FACILITY_AGREEMENT:REVERSE_FACTORING': 'Facility Agreement - Reverse Factoring',
    'FACILITY_AGREEMENT:REVOLVING_CREDIT_FACILITY': 'Facility Agreement - Revolving Credit Facility',
    'FACILITY_AGREEMENT:SECURITY': 'Facility Agreement - Security',
    'FACILITY_AGREEMENT:TRADE_PAYABLES_FINANCING': 'Facility Agreement - Trade Payables Financing',
    'IAS': 'Identity of Authorised Signatory',
    'internal_comment_doc': 'Internal Comment Document',
    'LF': 'Logistics Finance',
    'master_bill_of_lading': 'Master Bill of Lading',
    'other_document': 'Other Document',
    'packing_list': 'Packing List',
    'POA': 'Proof of Authority',
    'PRD': 'Passports of Registered Directors',
    'proforma_invoice': 'Proforma Invoice',
    'purchase_order': 'Purchase Order',
    'remarks_document': 'Remarks Document',
    'ROD': 'Register of Directors',
    'ROS': 'Register of Shareholders',
    'sales_contract': 'Sales Contract',
    'seaway_bill': 'Seaway Bill',
    'SL': 'Shipping List',
    'SLIST': 'Supplier List',
    'supplier_agreement': 'Supplier Agreement',
    'term_sheet_document': 'Term Sheet Document',
    'termSheetTermsDocument': 'Term Sheet Terms Document',
    'trade_document': 'Trade Document',
    'transaction_monitoring': 'Transaction Monitoring',
    'transaction_receipt': 'Transaction Receipt',
    'transport_document': 'Transport Document',
    'truck_consignment_note': 'Truck Consignment Note',
    'VTL': 'Valid Trade License',
    'YTDFS': 'Year To Date Financial Statement',
    'ARTICLES_OF_ASSOCIATION': 'Articles of Association',
    'TRADE_LICENSE': 'Trade License',

    // Newly added
    'FACILITY_AGREEMENT:PURCHASE_ORDER_FINANCING_CLIENT': 'Facility Agreement - Purchase Order Financing Client',
    'FACILITY_AGREEMENT:INVENTORY_FINANCE_SELLER': 'Facility Agreement - Inventory Finance Seller',
    'FACILITY_AGREEMENT:INVENTORY_FINANCE': 'Facility Agreement - Inventory Finance',
    'FACILITY_AGREEMENT:MRPA_CLIENT': 'Facility Agreement - MRPA Client',
    'FACILITY_AGREEMENT:PURCHASE_ORDER_FINANCING_CP': 'Facility Agreement - Purchase Order Financing Counterparty',
    'FACILITY_AGREEMENT:BUYER_LED_PO_TO_PAYABLE_FINANCE': 'Facility Agreement - Buyer Led PO to Payable Finance',
    'FACILITY_AGREEMENT:REVERSE_FACTORING_SELLER': 'Facility Agreement - Reverse Factoring Seller',
    'FACILITY_AGREEMENT:SCF_DPWFS': 'Facility Agreement - SCF DPWFS',

    'financialDecisioningDocument:finRatios': 'Financial Decisioning Document - Financial Ratios',
    'financialDecisioningDocument:creditMemo': 'Financial Decisioning Document - Credit Memo',
    'financialDecisioningDocument:riskRating': 'Financial Decisioning Document - Risk Rating',
    'financialDecisioningDocument:standardFinFormat': 'Financial Decisioning Document - Standard Financial Format',
  };

  public static getDocumentTypeLabel(value: string): string {
    if (!value) return '-';
    const label = ConstantsService.facilityDocumentTypeLabelMap[value];
    if (label) return label;
    const upperValue = value.toUpperCase();
    const upperLabel = ConstantsService.facilityDocumentTypeLabelMap[upperValue];
    if (upperLabel) return upperLabel;
    return ConstantsService.formatDocumentTypeLabel(value);
  }

  public static formatDocumentTypeLabel(value: string): string {
    if (!value) return '-';
    return value
      .replace(/:/g, ' - ')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  public clientCategorisationColourCode = {
    'Standard': '#6FD420',
    'Watch List': '#6E6E72',
    'Special Mention': '#3230BE',
    'Substandard': '#0F0F19',
    'Doubtful': '#FFC90C',
    'Loss': '#FF5F5F',
  };

  public termSheetKycQuestionKeyMapping = {
    balanceSheet: 'Does the Registrant recent financial has a Balance Sheet total of no less than USD20million in equivalent?',
    annualTurnover: 'Does the Registrant have a net annual turnover of no less than USD40million in equivalent?',
    capitalFunds: 'Does the Registrant have its own funds or called up capital of no less than USD2million or its equivalent? ',
    siteVisit: 'Has the relationship manager visited the premise where the client conducts business (office / factory)?',
    experienceInTradeFinance: 'Length of time dealing in Trade Finance',
  }

  public termSheetKycAnswerKeyMapping = {
    LESS_THAN_2_YEARS: 'Less than 2 years',
    BETWEEN_2_AND_5_YEARS: 'Between 2-5 years',
    MORE_THAN_5_YEARS: 'More than 5 years',
    THIRD_PARTY: 'Relying on group or third party to carry out site visit',
    YES: 'Yes',
    NO: 'No'
  }

  public kycPageTootTipMapping = {
    COMPANY_BUSINESS_TYPE: [
      { key: 'SP', value: 'Sole Proprietorship', desc: 'Refers to an unincorporated business with a single owner who shall pay personal income tax on profits earned from the business.' },
      { key: 'FO', value: 'Family Owned ', desc: 'Refers to an incorporated business or partnership where the members of the shareholders are owned by a family.' },
      { key: 'PR', value: 'Partnership', desc: 'Refers to a formal agreement between two or more people agreeing to be the co-owners, distribute responsibilities for running an organization, and share the income and/or losses that the business generates. The Partners are liable for each other.' },
      { key: 'PCSO', value: 'Private Company – Sole Ownership', desc: 'To be provided refers to an incorporated business whose ownership belong to a single individual.' },
      { key: 'LLP', value: 'Limited Liability Partnership (LLP)', desc: `This is a hybrid of partnership and corporation. It is a partnership in which some or all partners have limited liabilities. It therefore can exhibit elements of partnerships and corporations. In an LLP, each partner is not responsible or liable for another partner's misconduct or negligence.` },
      { key: 'PC', value: 'Public Company', desc: 'A public company, publicly traded company, publicly held company, publicly listed company, or public limited company is a company whose ownership is organized via shares of stock which are intended to be freely traded on a stock exchange or in over-the-counter markets.' },
      { key: 'LLCM', value: 'Limited Liability Company – Multiple Shareholders', desc: 'A legal entity structured to protect its owners from personal responsibility for its debts or liabilities. Limited liability companies are hybrid entities that combine the characteristics of a corporation with those of a partnership or sole proprietorship.' },
    ],
    JURISDICTION_OF_ESTABLISHMENT: [
      { key: 'ONSHORE', value: 'Onshore', desc: 'Refers to legal entities (unincorporated and incorporated) which are NOT established in any special economic zones, tax heaven and/or any free zones.' },
      { key: 'SPECIAL_ECONOMIC_ZONE', value: 'Special Economic Zone(s)', desc: 'Refers to legal entities (unincorporated and incorporated) which are established in special economic zones including free zones. ' },
      { key: 'TAX_HAVENS', value: 'Tax havens', desc: 'Refers to legal entities established in a country or jurisdiction that offer minimal tax liability to foreign business or individuals.' },
    ]
  }

}


export class Country {
  constructor(public country_name: string, public country_code: string, public selected?: boolean) {
    if (selected === undefined) selected = false;
  }
}
